import { orderClient, useQuery } from "@trueskin-web/apis"
import { useAuth } from "@trueskin-web/context"
import { formatCurrency } from "@trueskin-web/functions"
import { useTranslation } from "@trueskin-web/translations"

const OrderSummaryDetails = ({
  component: Component,
  repurchase,
  orderSummary,
}) => {
  const { t } = useTranslation()

  const {
    user: {
      addresses: [shippingAddress],
      billingAddresses: [billingAddress],
      isShippingAddressSameAsBillingAddress,
    },
  } = useAuth()

  const shippingCountry = isShippingAddressSameAsBillingAddress
    ? billingAddress.country
    : shippingAddress.country

  const isSwissShipping = shippingCountry === "CH"

  const voucher = orderSummary.vouchers.find(
    ({ systemDefault, stackable }) => !(systemDefault || stackable)
  )

  const voucherDetails =
    orderSummary.occurrence !== "occ_once" && voucher
      ? {
          code: voucher.voucher.code,
          message: voucher.discountGroupVariant?.definition.confirmationMessage,
          error: voucher.errors[0]?.message,
        }
      : undefined

  const referralDetails =
    orderSummary.occurrence !== "occ_once" && orderSummary.referral
      ? {
          code: orderSummary.referral.code,
          message: orderSummary.referral.message,
        }
      : undefined

  const alternativeDetails = isSwissShipping
    ? {
        alternativeTotalPrice: formatCurrency(
          Number.parseInt(
            orderSummary.values.otherCurrencies.find(
              ({ currency }) => currency === "EUR"
            ).total
          ) / 100,
          "EUR",
          2
        ),
        alternativeDescription: repurchase
          ? t("Payment.Overview.Repurchase.footnote")
          : t("Payment.Overview.Purchase.footnote"),
      }
    : null

  const instalmentsDetails = {
    instalmentsCount: orderSummary.instalments.count,
    perInstalmentPrice: formatCurrency(
      Number.parseInt(orderSummary.values.perInstalment.total) / 100,
      orderSummary.values.perInstalment.currency
    ),
    instalmentsTotalPrice: formatCurrency(
      Number.parseInt(orderSummary.values.total) / 100,
      orderSummary.values.currency
    ),
  }

  const servicesOrder = ["other", "medical_service", "addon", "shipping"]
  const discountServices = orderSummary.lineItems.filter(
    (item) => item.type === "discount"
  )
  const isAntiAgingPreset = orderSummary.preset.id.includes("anti_aging")

  const purchaseDetails = {
    repurchase,
    isSwissShipping,
    voucher: referralDetails || voucherDetails,

    presetId: orderSummary.preset.id,
    image: orderSummary.preset.details.image,
    title: orderSummary.preset.details.name,
    productTitle:
      orderSummary.instalments.count === 2
        ? t("TwoMonthsPresets.title", {
            count: orderSummary.instalments.count,
          })
        : orderSummary.preset.details.title,
    productDescription: orderSummary.preset.details.description,

    services: servicesOrder
      .reduce((acc, type) => {
        const item = orderSummary.lineItems.find((item) => item.type === type)
        if (!item) {
          return acc
        }
        return [
          ...acc,
          {
            ...item,
            // TODO @julianna: remove this when we have a proper solution for the anti-aging preset
            title:
              isAntiAgingPreset && type === "other"
                ? orderSummary.preset.details.name
                : item.title,
          },
        ]
      }, [])
      .concat(discountServices)
      .map(({ amount, currency, ...item }) => ({
        ...item,
        price: formatCurrency(Number.parseInt(amount) / 100, currency, 2),
      })),

    totalPrice: formatCurrency(
      Number.parseInt(orderSummary.values.total) /
        orderSummary.instalments.count /
        100,
      orderSummary.values.currency,
      2
    ),

    totalWithoutDiscounts: formatCurrency(
      orderSummary.values.totalWithoutDiscounts /
        orderSummary.instalments.count /
        100,
      orderSummary.values.currency,
      2
    ),

    isSubscription: orderSummary.occurrence !== "occ_once",

    ...alternativeDetails,
    ...instalmentsDetails,
  }

  return <Component {...purchaseDetails} />
}

const withOrderSummary = (component) => {
  const WrappedComponent = ({ repurchase }) => {
    const { data: { data: orderSummaryData } = {} } = useQuery(
      "currentOrderSummary",
      orderClient.getCurrentSummary
    )

    return (
      <OrderSummaryDetails
        component={component}
        repurchase={repurchase}
        orderSummary={orderSummaryData}
      />
    )
  }

  WrappedComponent.displayName = `withOrderSummary(${component.displayName})`

  return WrappedComponent
}

export default withOrderSummary
